import { Button, Drawer, Group, NumberInput, Stack, Text, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateFloor, useUpdateFloor } from 'src/Hooks/Mutations/useCartographyMutations';

import { Props } from './FloorDrawer.types';

const FloorDrawer = ({ opened, buildingId, floor, onClose }: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [level, setLevel] = useState<number | ''>(0);

  const isEdit = !!floor;

  useEffect(() => {
    if (opened) {
      setName(floor?.name ?? '');
      setLevel(floor?.level ?? 0);
    }
  }, [opened, floor]);

  const { mutate: createFloor, isLoading: isCreating } = useCreateFloor({ onSuccess: onClose });
  const { mutate: updateFloor, isLoading: isUpdating } = useUpdateFloor({ onSuccess: onClose });

  const handleSubmit = () => {
    if (!name.trim() || level === '') return;
    const payload = { name: name.trim(), level: Number(level) };
    if (isEdit && floor) {
      updateFloor({ floorId: floor.id, payload });
    } else {
      createFloor({ buildingId, payload });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      padding={20}
      title={
        isEdit
          ? t('admin.structure.floors.modal.editTitle', 'Modifier le niveau')
          : t('admin.structure.floors.modal.createTitle', 'Nouveau niveau')
      }
    >
      <Stack gap="md">
        <TextInput
          label={t('admin.structure.floors.modal.name', 'Nom')}
          placeholder={t('admin.structure.floors.modal.namePlaceholder', 'Rez-de-chaussée')}
          value={name}
          onChange={e => setName(e.currentTarget.value)}
          required
          data-autofocus
        />
        <div>
          <NumberInput
            label={t('admin.structure.floors.modal.level', 'Numéro de niveau')}
            value={level}
            onChange={value => setLevel(value === '' ? '' : Number(value))}
            required
          />
          <Text size="xs" c="dimmed" mt={4}>
            {t('admin.structure.floors.modal.levelHelp', '0 = rdc · 1 = 1er étage · -1 = sous-sol')}
          </Text>
        </div>
        <Group justify="space-between">
          <Button variant="outline" onClick={onClose}>
            {t('admin.structure.floors.modal.cancel', 'Annuler')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isCreating || isUpdating}
            disabled={!name.trim() || level === ''}
          >
            {t('admin.structure.floors.modal.save', 'Enregistrer')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
};

export default FloorDrawer;
